import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useMatch, useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import {
  getFreightDistanceDuration,
  getFreightOrderBrands,
  getFreightOrderCreateMeta,
  getFreightOrderEdit,
  getFreightOrderGradeCodes,
  getFreightOrderProcesses,
  searchFreightOrderAreas,
  storeFreightOrder,
  updateFreightOrder
} from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const nz = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

function AreaSearchBlock({ label, selected, onClear, query, onQueryChange, results, onPick }) {
  return (
    <div className="relative max-w-lg">
      <label className="block text-sm text-gray-600 mb-1">{label}</label>
      {selected ? (
        <div className="flex items-center gap-2 flex-wrap rounded border border-gray-200 bg-white px-3 py-2">
          <span>
            {selected.name}, {selected.city_name} · {selected.pincode}
          </span>
          <button type="button" className="btn-outline text-xs py-1" onClick={onClear}>
            Change
          </button>
        </div>
      ) : (
        <>
          <input
            className="w-full px-3 py-2 border border-gray-200 rounded"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Type at least 2 characters…"
            autoComplete="off"
          />
          {results.length > 0 && (
            <ul className="absolute z-20 left-0 right-0 mt-1 max-h-52 overflow-auto rounded border border-gray-200 bg-white shadow text-sm">
              {results.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    onClick={() => onPick(a)}
                  >
                    {a.name} — {a.city_name}, {a.state_name} ({a.pincode})
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

const FreightOrderFormPage = () => {
  const navigate = useNavigate();
  const newMatch = useMatch({ path: "/freight/orders/new", end: true });
  const { id: editOrderId } = useParams();
  const isNew = Boolean(newMatch);
  const orderId = isNew ? null : editOrderId;

  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Orders", link: "/freight/orders" },
      { label: isNew ? "New order" : `Edit #${orderId ?? ""}` }
    ],
    [roleLabel, isNew, orderId]
  );

  const [pageLoading, setPageLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [gradeList, setGradeList] = useState([]);
  const [processList, setProcessList] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [gradeCodeList, setGradeCodeList] = useState([]);

  const [orderName, setOrderName] = useState("");
  const [loadQty, setLoadQty] = useState("");

  const [fromQuery, setFromQuery] = useState("");
  const [toQuery, setToQuery] = useState("");
  const [fromResults, setFromResults] = useState([]);
  const [toResults, setToResults] = useState([]);
  const [fromSel, setFromSel] = useState(null);
  const [toSel, setToSel] = useState(null);

  const [gradeId, setGradeId] = useState("");
  const [processTypeId, setProcessTypeId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [gradeCodeId, setGradeCodeId] = useState("");

  const [distanceText, setDistanceText] = useState("");
  const [durationText, setDurationText] = useState("");

  const runAreaSearch = useCallback(async (term, which) => {
    const t = String(term).trim();
    if (t.length < 2) {
      if (which === "from") setFromResults([]);
      else setToResults([]);
      return;
    }
    try {
      const res = await searchFreightOrderAreas({ search: t });
      const list = Array.isArray(res?.data) ? res.data : [];
      if (which === "from") setFromResults(list);
      else setToResults(list);
    } catch {
      if (which === "from") setFromResults([]);
      else setToResults([]);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runAreaSearch(fromQuery, "from"), 300);
    return () => clearTimeout(t);
  }, [fromQuery, runAreaSearch]);

  useEffect(() => {
    const t = setTimeout(() => runAreaSearch(toQuery, "to"), 300);
    return () => clearTimeout(t);
  }, [toQuery, runAreaSearch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!fromSel?.id || !toSel?.id || fromSel.id === toSel.id) {
        setDistanceText("");
        setDurationText("");
        return;
      }
      try {
        const res = await getFreightDistanceDuration({
          from_id: String(fromSel.id),
          to_id: String(toSel.id)
        });
        if (cancelled) return;
        setDistanceText(res?.distance_text || "");
        setDurationText(res?.duration_text || "");
      } catch {
        if (!cancelled) {
          setDistanceText("");
          setDurationText("");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fromSel, toSel]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const gid = nz(gradeId);
      if (!gid) {
        setProcessList([]);
        return;
      }
      try {
        const res = await getFreightOrderProcesses({ grade_id: gid });
        if (cancelled) return;
        setProcessList(Array.isArray(res?.processData) ? res.processData : []);
      } catch {
        if (!cancelled) setProcessList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gradeId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const gid = nz(gradeId);
      const pid = nz(processTypeId);
      if (!gid || !pid) {
        setBrandList([]);
        return;
      }
      try {
        const res = await getFreightOrderBrands({ grade_id: gid, process_type_id: pid });
        if (cancelled) return;
        setBrandList(Array.isArray(res?.brandsData) ? res.brandsData : []);
      } catch {
        if (!cancelled) setBrandList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gradeId, processTypeId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const gid = nz(gradeId);
      const pid = nz(processTypeId);
      const bid = nz(brandId);
      if (!gid || !pid || !bid) {
        setGradeCodeList([]);
        return;
      }
      try {
        const res = await getFreightOrderGradeCodes({
          grade_id: gid,
          process_type_id: pid,
          brand_id: bid
        });
        if (cancelled) return;
        setGradeCodeList(Array.isArray(res?.gradeCodeData) ? res.gradeCodeData : []);
      } catch {
        if (!cancelled) setGradeCodeList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gradeId, processTypeId, brandId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPageLoading(true);
      try {
        if (isNew) {
          const res = await getFreightOrderCreateMeta();
          const data = res?.data ?? res;
          setGradeList(Array.isArray(data?.gradeList) ? data.gradeList : []);
        } else if (orderId) {
          const res = await getFreightOrderEdit(orderId);
          const data = res?.data ?? res;
          const o = data?.freightOrderData;
          setGradeList(Array.isArray(data?.gradeList) ? data.gradeList : []);
          if (o) {
            setOrderName(o.order_name != null ? String(o.order_name) : "");
            setLoadQty(o.load_qty != null ? String(o.load_qty) : "");
            setGradeId(o.grade_id != null ? String(o.grade_id) : "");
            setProcessTypeId(o.process_type_id != null ? String(o.process_type_id) : "");
            setBrandId(o.brand_id != null ? String(o.brand_id) : "");
            setGradeCodeId(o.grade_code_id != null ? String(o.grade_code_id) : "");
          }
          const fl = data?.fromLocationData;
          const tl = data?.toLocationData;
          if (fl && fl.id) {
            setFromSel({
              id: fl.id,
              name: fl.name,
              city_name: fl.city_name,
              state_name: fl.state_name,
              pincode: fl.pincode
            });
          }
          if (tl && tl.id) {
            setToSel({
              id: tl.id,
              name: tl.name,
              city_name: tl.city_name,
              state_name: tl.state_name,
              pincode: tl.pincode
            });
          }
        }
      } catch (e) {
        showError(e.message || "Failed to load form");
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, orderId]);

  const onGradeChange = (v) => {
    setGradeId(v);
    setProcessTypeId("");
    setBrandId("");
    setGradeCodeId("");
  };

  const onProcessChange = (v) => {
    setProcessTypeId(v);
    setBrandId("");
    setGradeCodeId("");
  };

  const onBrandChange = (v) => {
    setBrandId(v);
    setGradeCodeId("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromSel?.id || !toSel?.id) {
      showError("Pick both origin and destination areas.");
      return;
    }
    const lq = String(loadQty).trim();
    if (!lq) {
      showError("Load quantity is required.");
      return;
    }
    const bodyBase = {
      order_name: orderName.trim() || undefined,
      load_qty: lq,
      from_location_id: fromSel.id,
      to_location_id: toSel.id,
      grade_id: nz(gradeId),
      process_type_id: nz(processTypeId),
      brand_id: nz(brandId),
      grade_code_id: nz(gradeCodeId)
    };
    setSubmitting(true);
    try {
      if (isNew) {
        const res = await storeFreightOrder(bodyBase);
        const nid = res?.data?.id ?? res?.data?.data?.id;
        showSuccess(res?.message || "Order created");
        if (nid != null) navigate(`/freight/orders/${nid}`);
        else navigate("/freight/orders");
      } else {
        await updateFreightOrder({
          ...bodyBase,
          freight_order_id: Number(orderId)
        });
        showSuccess("Order updated");
        navigate(`/freight/orders/${orderId}`);
      }
    } catch (err) {
      showError(err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[960px] space-y-4">
        <Link to="/freight/orders" className="btn-outline inline-block" style={{ textDecoration: "none" }}>
          ← Orders
        </Link>

        {pageLoading && <div className="card offers-table p-6 text-gray-600">Loading form…</div>}

        {!pageLoading && (
          <form className="card offers-table border-l-4 border-l-[#064E8D] space-y-6" onSubmit={handleSubmit}>
            <div>
              <h2 className="m-0 text-xl font-semibold">{isNew ? "Create freight order" : "Edit freight order"}</h2>
              <p className="text-sm text-gray-600 m-0 mt-1">
                Pick areas (min. 2 letters search), quantity, and optional material. Distance updates when both areas
                are set.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-gray-600">Order name (optional)</span>
                <input
                  className="px-3 py-2 border border-gray-200 rounded"
                  value={orderName}
                  onChange={(e) => setOrderName(e.target.value)}
                  placeholder="e.g. Mumbai to Delhi"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-gray-600">Load quantity (MT) *</span>
                <input
                  className="px-3 py-2 border border-gray-200 rounded"
                  value={loadQty}
                  onChange={(e) => setLoadQty(e.target.value)}
                  placeholder="e.g. 25"
                  required
                />
              </label>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AreaSearchBlock
                label="From area *"
                selected={fromSel}
                onClear={() => {
                  setFromSel(null);
                  setFromQuery("");
                  setFromResults([]);
                }}
                query={fromQuery}
                onQueryChange={setFromQuery}
                results={fromResults}
                onPick={(a) => {
                  setFromSel(a);
                  setFromResults([]);
                  setFromQuery("");
                }}
              />
              <AreaSearchBlock
                label="To area *"
                selected={toSel}
                onClear={() => {
                  setToSel(null);
                  setToQuery("");
                  setToResults([]);
                }}
                query={toQuery}
                onQueryChange={setToQuery}
                results={toResults}
                onPick={(a) => {
                  setToSel(a);
                  setToResults([]);
                  setToQuery("");
                }}
              />
            </div>

            {(distanceText || durationText) && (
              <div className="rounded-lg bg-slate-50 border border-slate-100 px-4 py-3 text-sm text-gray-800">
                <strong>Approx.</strong> {distanceText || "—"}
                {durationText ? ` · ${durationText}` : ""}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-gray-600">Grade</span>
                <select
                  className="px-3 py-2 border border-gray-200 rounded bg-white"
                  value={gradeId}
                  onChange={(e) => onGradeChange(e.target.value)}
                >
                  <option value="">—</option>
                  {gradeList.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-gray-600">Process</span>
                <select
                  className="px-3 py-2 border border-gray-200 rounded bg-white"
                  value={processTypeId}
                  onChange={(e) => onProcessChange(e.target.value)}
                  disabled={!nz(gradeId)}
                >
                  <option value="">—</option>
                  {processList.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-gray-600">Brand</span>
                <select
                  className="px-3 py-2 border border-gray-200 rounded bg-white"
                  value={brandId}
                  onChange={(e) => onBrandChange(e.target.value)}
                  disabled={!nz(processTypeId)}
                >
                  <option value="">—</option>
                  {brandList.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-gray-600">Grade code</span>
                <select
                  className="px-3 py-2 border border-gray-200 rounded bg-white"
                  value={gradeCodeId}
                  onChange={(e) => setGradeCodeId(e.target.value)}
                  disabled={!nz(brandId)}
                >
                  <option value="">—</option>
                  {gradeCodeList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving…" : isNew ? "Create order" : "Save changes"}
              </button>
              <Link to={isNew ? "/freight/orders" : `/freight/orders/${orderId}`} className="btn-outline">
                Cancel
              </Link>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FreightOrderFormPage;
